import * as React from "react";
import { setPersisted, getPersisted } from "../../../../utils/storage";
import { sortMonitoringData } from "../../../../utils/sorting";
import {
  monitoringColumns,
  Direction,
  Columns,
} from "../../../../utils/columns";

type PersistedSort = {
  option: Columns;
  direction: Direction;
};

const DEFAULT_LOAD_COUNT = 10;

export function useGridState(
  occurrences: Insite.Insights.ComposedInsight[],
  filtered: Insite.Insights.ComposedOccurrence[],
  persistKey?: string
) {
  const totalCount = occurrences.length;
  const defaultCount =
    totalCount < DEFAULT_LOAD_COUNT ? totalCount : DEFAULT_LOAD_COUNT;
  let initialSortDir = monitoringColumns.LATEST.sortDirection;
  let initialSortOption = Columns.LATEST;
  const firstRender = React.useRef(true);
  if (persistKey && firstRender.current) {
    firstRender.current = false;
    const sort = getPersisted<PersistedSort | undefined>(
      { key: "Monitoring-colSort", prop: persistKey },
      undefined
    );
    if (sort !== undefined) {
      initialSortDir = sort.direction;
      initialSortOption = sort.option;
    }
  }

  const [sorted, setSorted] = React.useState<Insite.Insights.ComposedInsight[]>(
    []
  );
  const [currentCount, setCurrentCount] = React.useState(defaultCount);
  const [sort, setSort] = React.useState<PersistedSort>({
    option: initialSortOption,
    direction: initialSortDir,
  });

  const handleSort =
    (activeSort: Columns, shouldUpdateDirection = false) =>
    (sortFuncKey: Columns) => {
      let direction = monitoringColumns[sortFuncKey].sortDirection;
      if (activeSort === sortFuncKey && shouldUpdateDirection) {
        direction = sort.direction === "asc" ? "desc" : "asc";
      } else {
        direction = sort.direction;
      }

      if (persistKey) {
        setPersisted(
          { key: "Monitoring-colSort", prop: persistKey },
          { option: sortFuncKey, direction: direction }
        );
      }

      let sorted = sortMonitoringData(occurrences, sortFuncKey, direction);
      setSort({ option: sortFuncKey, direction });
      setSorted(sorted);
    };

  React.useEffect(() => {
    handleSort(sort.option, false)(sort.option);
  }, [occurrences]);

  React.useEffect(() => {
    let newCount;
    if (totalCount < DEFAULT_LOAD_COUNT) {
      newCount = totalCount;
    } else if (currentCount > totalCount) {
      newCount = totalCount;
    } else if (
      totalCount > DEFAULT_LOAD_COUNT &&
      currentCount >= DEFAULT_LOAD_COUNT
    ) {
      newCount = currentCount;
    } else {
      newCount = DEFAULT_LOAD_COUNT;
    }
    setCurrentCount(newCount);
  }, [totalCount, filtered]);

  return {
    activeSort: sort.option,
    currentCount,
    defaultCount: DEFAULT_LOAD_COUNT,
    sorted,
    setCurrentCount,
    sortDirection: sort.direction,
    totalCount,
    onSort: handleSort,
  };
}
